import { Expert, GeneratedImage, UiStreamEvent } from '../types';
import { createAuthenticatedRequestOptions } from '../utils/apiUtils';

const getStage = (): string => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('cloudfront.net') || hostname.includes('dev-askbell')) {
    return 'dev';
  }

  if (hostname === "askbell.bellomyonline.com") {
    return 'prod';
  }
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'dev';
  }
  
  return 'dev';
};

const stage = getStage();
export const API_BASE_URL = stage === 'prod' 
  ? 'https://rr3n1byep6.execute-api.us-east-1.amazonaws.com/prod'
  : 'https://2az2fpvgf5.execute-api.us-east-2.amazonaws.com/dev';

// Use the centralized function for creating authenticated request options
const createRequestOptions = createAuthenticatedRequestOptions;

// Helper to convert UPPER_SNAKE_CASE or mixed keys to camelCase and handle new expert structure
function normalizeExpertKeys(expert: any): any {
  const keyMap: Record<string, string> = {
    'ID': 'id',
    'NAME': 'name',
    'ARCHETYPE': 'archetype',
    'BACKGROUND': 'background', 
    'DESCRIPTION': 'description',
    'MODEL': 'model',
    'URL': 'url',
    'SYSTEM_PROMPT': 'systemPrompt',
    // Add more mappings as needed
  };
  const normalized: any = {};
  for (const key in expert) {
    if (Object.prototype.hasOwnProperty.call(expert, key)) {
      const camelKey = keyMap[key] || key.charAt(0).toLowerCase() + key.slice(1);
      normalized[camelKey] = expert[key];
    }
  }
  // Support snake_case coming from backend or S3
  if (!normalized.systemPrompt && typeof expert.system_prompt === 'string') {
    normalized.systemPrompt = expert.system_prompt;
  }
  
  // Ensure backward compatibility: if no name but has archetype, copy archetype to name
  if (!normalized.name && normalized.archetype) {
    normalized.name = normalized.archetype;
  }
  
  // Ensure forward compatibility: if no archetype but has name, copy name to archetype
  if (!normalized.archetype && normalized.name) {
    normalized.archetype = normalized.name;
  }
  
  return normalized;
}

export const apiService = {
  async loadExperts(forceRefresh: boolean = false): Promise<Expert[]> {
    try {
      // Check for manual refresh trigger in URL
      const urlParams = new URLSearchParams(window.location.search);
      const urlRefresh = urlParams.get('REFRESH_EXPERTS')?.toUpperCase() === 'TRUE';
      const shouldRefresh = forceRefresh || urlRefresh;
      
      // Build URL with refresh parameter if needed
      let url = `${API_BASE_URL}/api/experts`;
      if (shouldRefresh) {
        url += '?REFRESH_EXPERTS=TRUE';
      }
      
      const response = await fetch(url, createRequestOptions());
      if (!response.ok) {
        throw new Error(`Failed to load experts: ${response.status}`);
      }
      const data = await response.json();
      let loadedExperts: any[] = [];
      if (Array.isArray(data)) {
        loadedExperts = data;
      } else if (data && Array.isArray(data.experts)) {
        loadedExperts = data.experts;
      } else {
        throw new Error('Malformed experts response');
      }
      
      // With the new system, the backend handles all expert logic including defaults
      // So we just normalize and return what we get
      const normalizedExperts = loadedExperts.map(normalizeExpertKeys);
      return normalizedExperts;
    } catch (error) {
      console.error('Error loading experts:', error);
      // Return default experts as final fallback
      const defaultExpertsRaw = [
        {
          ID: 'default-general-ai',
          NAME: 'General AI',
          ARCHETYPE: 'General AI',
          BACKGROUND: 'A general-purpose AI assistant',
          DESCRIPTION: 'AI assistant for all topics',
          MODEL: 'gpt-4.1',
        },
        {
          ID: 'default-reasoning-ai',
          NAME: 'General Reasoning AI',
          ARCHETYPE: 'General Reasoning AI',
          BACKGROUND: 'A general-purpose reasoning AI assistant',
          DESCRIPTION: 'Reasoning AI assistant for all topics',
          MODEL: 'gpt-5',
        },
        {
          ID: 'default-multitool-ai',
          NAME: 'Multitool AI',
          ARCHETYPE: 'Multitool AI',
          BACKGROUND: 'A multitool AI assistant',
          DESCRIPTION: 'ChatGPT 5 like experience.',
          MODEL: 'gpt-5',
        }
      ];
      return defaultExpertsRaw.map(normalizeExpertKeys);
    }
  },

  async initializeChat(expert: Expert): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/initialize`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ experts: [expert.name || expert.archetype] })
      }));

      if (!response.ok) {
        throw new Error(`Failed to initialize chat: ${response.status}`);
      }

      const data = await response.json();
      return data.chatId;
    } catch (error) {
      // console.error('Error initializing chat:', error);
      throw error;
    }
  },

  async getPresignedUploadUrl(chatId: string, file: File): Promise<{ url?: string; key: string; uploadRequired: boolean }> {
    // console.log(`Requesting presigned URL for file: ${file.name}, type: ${file.type}, size: ${file.size}`);
    const response = await fetch(`${API_BASE_URL}/api/chat/${encodeURIComponent(chatId)}/attachments/presign`, createRequestOptions({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type })
    }));
    if (!response.ok) {
      throw new Error('Failed to get presigned upload URL');
    }
    const result = await response.json();
    // console.log(`Presigned URL response:`, result);
    return result;
  },

  async uploadFileToS3(
    url: string, 
    file: File, 
    contentType: string, 
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };
      }
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('Failed to upload file to S3'));
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Failed to upload file to S3'));
      };
      
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  },

  async sendMessage(
    message: string,
    expert: Expert,
    attachedFiles: File[] = [],
    chatId?: string,
    fromMessageIndex?: number,
    setOperationStatus?: (status: '' | 'securing-transfer' | 'uploading-attachments' | 'thinking') => void,
    onFileProgress?: (fileName: string, progress: number) => void
  ): Promise<{ requestId: string; fileReferences: { name: string; type: string; size: number; key: string }[] }> {
    try {
      // Step 1: Upload files to S3 and collect their S3 keys
      const fileReferences: { name: string; type: string; size: number; key: string }[] = [];
      
      if (attachedFiles.length > 0) {
        // Set status to securing transfer when getting presigned URLs
        // console.log('API: Setting status to securing-transfer');
        setOperationStatus?.('securing-transfer');
        
        // Get all presigned URLs first
        const presignedData = await Promise.all(
          attachedFiles.map(file => this.getPresignedUploadUrl(chatId || '', file))
        );
        
        // Set status to uploading when actually uploading files
        const filesToUpload = presignedData.filter(data => data.uploadRequired && data.url);
        if (filesToUpload.length > 0) {
          // console.log('API: Setting status to uploading-attachments');
          setOperationStatus?.('uploading-attachments');
          
          // Upload all files in parallel with individual progress tracking
          await Promise.all(
            filesToUpload.map((data, index) => {
              const file = attachedFiles[presignedData.indexOf(data)];
              // console.log(`Uploading file ${file.name} to S3...`);
              return this.uploadFileToS3(data.url!, file, file.type, (progress) => {
                onFileProgress?.(file.name, progress);
              });
            })
          );
          // console.log(`Successfully uploaded ${filesToUpload.length} files to S3`);
        }
        
        // Collect all file references
        presignedData.forEach((data, index) => {
          const file = attachedFiles[index];
          fileReferences.push({ name: file.name, type: file.type, size: file.size, key: data.key });
        });
      }

      // Step 2: Send message with file references only
      // console.log('API: Setting status to thinking');
      setOperationStatus?.('thinking');
      
      const payload: any = {
        message,
        expert,
        chatId,
        fromMessageIndex,
        attachments: fileReferences.map(f => ({ name: f.name, type: f.type, size: f.size, key: f.key }))
      };
      // Remove undefined fields
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const response = await fetch(`${API_BASE_URL}/api/chat/single-expert/message`, createRequestOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }));

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();
      return { requestId: data.requestId, fileReferences };
    } catch (error) {
      // console.error('Error sending message:', error);
      throw error;
    }
  },

  async pollResponse(requestId: string, chatId: string): Promise<{ content: string; isComplete: boolean; images?: GeneratedImage[]; has_images?: boolean; response_id?: string; error?: string; hasError?: boolean; artifacts?: { name: string; url: string }[]; reasoning?: string; events?: UiStreamEvent[]; displayStatus?: string }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/chat/${chatId}/single-expert-status?requestId=${encodeURIComponent(requestId)}`,
        createRequestOptions()
      );

      if (!response.ok) {
        throw new Error(`Failed to poll response: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if this is an error response
      if (data.status === 'error') {
        return {
          content: '',
          isComplete: true,
          error: data.error,
          hasError: true
        };
      }
      
      const result: { content: string; isComplete: boolean; images?: GeneratedImage[]; has_images?: boolean; response_id?: string; error?: string; hasError?: boolean; artifacts?: { name: string; url: string }[]; reasoning?: string; events?: UiStreamEvent[]; displayStatus?: string } = {
        content: data.response || '',
        isComplete: data.status === 'complete',
        hasError: false
      };
      // Include streaming events for UI visibility if present
      if (Array.isArray(data.events) && data.events.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('[api.pollResponse] Received events count=', data.events.length, 'requestId=', requestId);
        result.events = data.events;
      }
      // Include transient reasoning text if present
      if (typeof data.reasoning === 'string' && data.reasoning.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('[api.pollResponse] Received reasoning len=', data.reasoning.length, 'requestId=', requestId);
        result.reasoning = data.reasoning;
      } else {
        // eslint-disable-next-line no-console
        console.debug('[api.pollResponse] No reasoning in response for requestId=', requestId);
      }
      // Include artifacts (download links) if present
      if (Array.isArray(data.artifacts) && data.artifacts.length > 0) {
        result.artifacts = data.artifacts.map((a: any) => ({ name: a.name, url: a.url }));
      }
      
      // Include image data if present
      if (data.has_images && data.images) {
        result.images = data.images;
        result.has_images = true;
      }
      
      // Include response ID for conversation chaining
      if (data.response_id) {
        result.response_id = data.response_id;
      }
      
      // Include async expert display status (e.g., "Extracting — reading documents...")
      if (typeof data.displayStatus === 'string' && data.displayStatus.length > 0) {
        result.displayStatus = data.displayStatus;
      }
      
      return result;
    } catch (error) {
      console.error('Error polling response:', error);
      throw error;
    }
  },

  async generateChatTitle(userMessage: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/generate-title`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question: userMessage })
      }));

      if (!response.ok) {
        throw new Error(`Failed to generate title: ${response.status}`);
      }

      const data = await response.json();
      return data.title || `Chat ${new Date().toLocaleDateString()}`;
    } catch (error) {
      // console.error('Error generating chat title:', error);
      return `Chat ${new Date().toLocaleDateString()}`;
    }
  },

  async checkMessageStatus(chatId: string, requestId?: string): Promise<{ content: string; isComplete: boolean } | null> {
    try {
      // Treat empty string as undefined (no requestId)
      const hasRequestId = requestId && requestId.trim() !== '';
      const url = hasRequestId 
        ? `${API_BASE_URL}/api/chat/${chatId}/single-expert-status?requestId=${encodeURIComponent(requestId!)}`
        : `${API_BASE_URL}/api/chat/${chatId}/single-expert-status`;
      
      const response = await fetch(url, createRequestOptions());

      if (!response.ok) {
        if (response.status === 404) {
          // No status found, message might be complete or never started
          return null;
        }
        throw new Error(`Failed to check message status: ${response.status}`);
      }

      const data = await response.json();
      
      // If status is pending and there's no content, it means no response was found
      if (data.status === 'pending' && !data.response) {
        return null;
      }
      
      return {
        content: data.response || '',
        isComplete: data.isComplete || data.status === 'complete'
      };
    } catch (error) {
      console.error('Error checking message status:', error);
      return null;
    }
  },

  async sendMessageWithPreUploadedFiles(
    message: string,
    expert: Expert,
    fileReferences: { name: string; type: string; size: number; key: string }[] = [],
    chatId?: string,
    fromMessageIndex?: number,
    setOperationStatus?: (status: '' | 'securing-transfer' | 'uploading-attachments' | 'thinking') => void
  ): Promise<{ requestId: string; fileReferences: { name: string; type: string; size: number; key: string }[] }> {
    try {
      // Skip file upload since files are already uploaded
      // Set status directly to thinking
      setOperationStatus?.('thinking');
      
      const payload: any = {
        message,
        expert,
        chatId,
        fromMessageIndex,
        attachments: fileReferences.map(f => ({ name: f.name, type: f.type, size: f.size, key: f.key }))
      };
      // Remove undefined fields
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const response = await fetch(`${API_BASE_URL}/api/chat/single-expert/message`, createRequestOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }));

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();
      return { requestId: data.requestId, fileReferences };
    } catch (error) {
      console.error('Error sending message with pre-uploaded files:', error);
      throw error;
    }
  },

  // PromptVault API methods
  async getPromptVault(limit: number = 20): Promise<{ name: string; content: string }[]> {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString()
      });
      
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault?${queryParams}`, createRequestOptions());
      if (!response.ok) {
        throw new Error(`Failed to get PromptVault: ${response.status}`);
      }
      const data = await response.json();
      return data.prompts || [];
    } catch (error) {
      // console.error('Error getting PromptVault:', error);
      throw error;
    }
  },

  async savePromptToVault(name: string, content: string, tags?: string[], sourceChatId?: string): Promise<void> {
    try {
      const body: any = { name, content };
      if (tags && tags.length > 0) {
        body.tags = tags;
      }
      if (sourceChatId) {
        body.source_chat_id = sourceChatId;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault/save`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      }));

      if (!response.ok) {
        throw new Error(`Failed to save prompt: ${response.status}`);
      }
    } catch (error) {
      // console.error('Error saving prompt:', error);
      throw error;
    }
  },

  async deletePromptFromVault(name: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault/delete`, createRequestOptions({
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      }));

      if (!response.ok) {
        throw new Error(`Failed to delete prompt: ${response.status}`);
      }
    } catch (error) {
      // console.error('Error deleting prompt:', error);
      throw error;
    }
  },

  async searchPromptVault(searchQuery: string, limit: number = 20): Promise<{ name: string; content: string }[]> {
    try {
      const queryParams = new URLSearchParams({
        q: searchQuery,
        limit: limit.toString()
      });
      
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault/search?${queryParams}`, createRequestOptions());
      if (!response.ok) {
        throw new Error(`Failed to search PromptVault: ${response.status}`);
      }
      const data = await response.json();
      return data.prompts || [];
    } catch (error) {
      // console.error('Error searching PromptVault:', error);
      throw error;
    }
  },

  async updatePromptUsage(name: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault/usage`, createRequestOptions({
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      }));

      if (!response.ok) {
        throw new Error(`Failed to update prompt usage: ${response.status}`);
      }
    } catch (error) {
      // console.error('Error updating prompt usage:', error);
      throw error;
    }
  },

  async togglePromptStar(name: string): Promise<{ success: boolean; isStarred: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prompt-vault/star`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      }));

      if (!response.ok) {
        throw new Error(`Failed to toggle prompt star: ${response.status}`);
      }

      const data = await response.json();
      return { success: data.success, isStarred: data.isStarred };
    } catch (error) {
      // console.error('Error toggling prompt star:', error);
      throw error;
    }
  }
}; 